import type { Activity, Contact } from '@/types/person/contacts';

import { contactsService } from '~/services/contacts-service';

export const useContactsStore = defineStore('contacts', () => {
    const nuxtApp = useNuxtApp();
    const auth = useAuthStore();
    const personStore = usePersonStore();
    const { showSuccess, showError, showWarning } = useGlobalToast();
    const selectedContact = ref<Contact | null>(null);
    const chatMessages = ref<Activity[]>([]);
    const searchTerm = ref('');
    const uploads = ref<{ name: string; size: string; progress: number }[]>([]);
    const capturedPhoto = ref<string | null>(null);

    //state
    const contacts = ref<Contact[]>([]);
    const searchQuery = ref('');

    const tags = ref([
        { name: 'Friends', code: 'friend' },
        { name: 'Gamers', code: 'gamer' },
        { name: 'Family', code: 'family' },
        { name: 'Coworkers', code: 'coworker' },
    ]);

    // Computed getter for filtered contacts
    const filteredContacts = computed(() => {
        let filtered = contacts.value;

        // Apply search filter
        if (searchQuery.value) {
            const lowerCaseTerm = searchQuery.value.toLowerCase();
            filtered = filtered.filter((contact) =>
                contact.name.toLowerCase().includes(lowerCaseTerm)
            );
        }

        return filtered;
    });

    async function fetchContacts() {
        if (!personStore.person) return;

        try {
            contacts.value  = await contactsService.getContacts(personStore.person.id);
        } catch (error: any) {
            showError("Error", error.message || 'Failed to fetch contacts');
        }
    }

    async function fetchActivities() {
        try {
            const activities = await contactsService.getActivities(10000);
            
            //only show values that have type: chat_message
            const filteredChat = activities.filter((item: Activity) => item.type === 'chat_message');

            //sort the chat messages by created_at
            filteredChat.sort((a: Activity, b: Activity) => {
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });

            chatMessages.value = filteredChat;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to fetch activities');
        }
    }

    async function sendMessage(message: object) {
        try {
            await contactsService.sendMessage(message);
            await fetchActivities();
        } catch (error: any) {
            showError("Error", error.message || 'Failed to send message');
        }
    }

    async function createContact(contactData: any) {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return false;
        }

        try {
            await contactsService.createContact(personStore.person.id, contactData);
            await fetchContacts(); // Refresh the contacts list
            showSuccess("Success", 'Contact created successfully');
            return true;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to create contact');
            return false;
        }
    }

    async function updateContact(contactId: number, contactData: any) {
        if (!personStore.person) {
            showError("Error", 'User not found');
            return false;
        }

        try {
            const updatedContact = await contactsService.updateContact(personStore.person.id, contactId, contactData);
            
            // Update the contact in the local state
            const index = contacts.value.findIndex(c => c.id === contactId);
            if (index !== -1) {
                contacts.value[index] = updatedContact;
            }
            
            showSuccess("Success", 'Contact updated successfully');
            return true;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to update contact');
            return false;
        }
    }

    const saveProfile = () => {
        showSuccess("Success", 'Profile saved successfully');
    };

    const createGroupChat = () => {
        showSuccess("Success", 'Group conversation created successfully');
    };

    const blockContact = () => {
        showSuccess("Success", 'Contact blocked successfully');
        selectedContact.value = null;
    };

    const unBlockContact = () => {
        showSuccess("Success", 'Contact unblocked successfully');
        selectedContact.value = null;
    };

    const removeContact = async (contact_id: number) => {
        if (!personStore.person) return;

        try {
            await contactsService.deleteContact(personStore.person.id, contact_id);
            
            // Only remove the contact from the contacts after successful API call
            contacts.value = contacts.value.filter(contact => contact.id !== contact_id);
            showSuccess("Success", 'Contact removed successfully');
            selectedContact.value = null;
        } catch (error: any) {
            showError("Error", error.message || 'Something went wrong while removing contact');
            // Don't update local state if the API call failed
        }
    };

    const searchContacts = (query: string) => {
        searchQuery.value = query;
    };

    const clearSearch = () => {
        searchQuery.value = '';
    };

    return {
        contacts,
        selectedContact,
        chatMessages,
        searchTerm,
        capturedPhoto,
        fetchContacts,
        fetchActivities,
        sendMessage,
        createContact,
        updateContact,
        tags,
        saveProfile,
        blockContact,
        removeContact,
        searchContacts,
        clearSearch,
        searchQuery,
        createGroupChat,
        uploads,
        filteredContacts,
        unBlockContact,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useContactsStore, import.meta.hot))
}